import { useCallback, useEffect, useState } from "react";

// What is open on a screen lives in the query string (docs/DESIGN.md, "Links and the URL"): a
// link to it opens it for a colleague, a reload keeps it, and Back closes it.
//
// OPENING ADDS A STEP; TYPING DOESN'T. Opening a row or choosing a person pushes a history entry,
// so Back undoes it. A search or a filter replaces the current entry (`replace`), so Back doesn't
// replay every keystroke.
//
// IDS ONLY. Never a person's name or email: URLs end up in logs, history and screenshots.

const CHANGED = "querychange";

export function readQuery(name: string): string | null {
  return new URLSearchParams(window.location.search).get(name);
}

/** Set one query value, from anywhere. Hooks reading it update. */
export function writeQuery(name: string, value: string | null, opts: { replace?: boolean } = {}): void {
  writeQueries({ [name]: value }, opts);
}

/** Set several at once, as one history step (switching a view and clearing what it showed). */
export function writeQueries(values: Record<string, string | null>,
                             opts: { replace?: boolean } = {}): void {
  const url = new URL(window.location.href);
  for (const [name, value] of Object.entries(values)) {
    if (value) url.searchParams.set(name, value); else url.searchParams.delete(name);
  }
  if (url.href === window.location.href) return;
  if (opts.replace) window.history.replaceState(window.history.state, "", url);
  else window.history.pushState(window.history.state, "", url);
  window.dispatchEvent(new Event(CHANGED));
}

/** One query value as state. Back and Forward, and `writeQuery` elsewhere, update it. */
export function useQueryParam(name: string):
    [string | null, (value: string | null, opts?: { replace?: boolean }) => void] {
  const [value, setValue] = useState(() => readQuery(name));
  useEffect(() => {
    const sync = () => setValue(readQuery(name));
    window.addEventListener("popstate", sync);
    window.addEventListener(CHANGED, sync);
    return () => {
      window.removeEventListener("popstate", sync);
      window.removeEventListener(CHANGED, sync);
    };
  }, [name]);
  const set = useCallback((v: string | null, opts?: { replace?: boolean }) => {
    writeQuery(name, v, opts);
    setValue(v);
  }, [name]);
  return [value, set];
}
